import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Button, Input, Label, Textarea
} from '../ui/UIComponents';
import { Application, ApplicationFormData } from '../../types/rbac.types';
import { createApplication, updateApplication } from '../../services/rbacApi';

interface ApplicationFormProps {
  isOpen: boolean;
  onClose: (refreshData?: boolean) => void;
  application: Application | null;
}

// Define a local interface that uses only string types for form inputs
interface ApplicationFormState {
  name: string;
  description: string;
  app_code: string;
  app_url: string;
  api_url: string;
  logo_url: string;
  status: 'active' | 'inactive' | 'development';
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({ isOpen, onClose, application }) => {
  const [formData, setFormData] = useState<ApplicationFormState>({
    name: '',
    description: '',
    app_code: '',
    app_url: '',
    api_url: '',
    logo_url: '',
    status: 'active'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Initialize form with application data if editing
  useEffect(() => {
    if (application) {
      setFormData({
        name: application.name,
        description: application.description || '',
        app_code: application.app_code,
        app_url: application.app_url || '',
        api_url: application.api_url || '',
        logo_url: application.logo_url || '',
        status: application.status
      });
    } else {
      setFormData({
        name: '',
        description: '',
        app_code: '',
        app_url: '',
        api_url: '',
        logo_url: '',
        status: 'active'
      });
    }
  }, [application, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.app_code.trim()) {
      newErrors.app_code = 'Application code is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.app_code)) {
      newErrors.app_code = 'Code must contain only uppercase letters, numbers, and underscores';
    }
    
    if (formData.app_url && !/^https?:\/\//.test(formData.app_url)) {
      newErrors.app_url = 'URL must start with http:// or https://';
    }
    
    if (formData.api_url && !/^https?:\/\//.test(formData.api_url)) {
      newErrors.api_url = 'API URL must start with http:// or https://';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Convert form state to ApplicationFormData
      const applicationData: ApplicationFormData = {
        name: formData.name,
        description: formData.description || undefined,
        app_code: formData.app_code,
        app_url: formData.app_url || undefined,
        api_url: formData.api_url || undefined,
        logo_url: formData.logo_url || undefined,
        status: formData.status
      };
      
      if (application) {
        // Update existing application
        await updateApplication(application.id, applicationData);
      } else {
        // Create new application
        await createApplication(applicationData);
      }
      
      onClose(true); // Close form and refresh data
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to save application');
      console.error('Error saving application:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div 
        className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 bg-gray-800/50">
          <h2 className="text-xl font-semibold text-white">
            {application ? 'Edit Application' : 'Create New Application'}
          </h2>
          <button
            onClick={() => onClose()}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {submitError && (
              <div className="mb-6 p-4 bg-red-900/30 border border-red-500 text-red-300 rounded-lg">
                {submitError}
              </div>
            )}
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <Label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter application name"
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-600'}`}
                />
                {errors.name && <p className="mt-2 text-sm text-red-400">{errors.name}</p>}
              </div>
              
              <div>
                <Label htmlFor="app_code" className="block text-sm font-medium text-gray-300 mb-2">
                  Application Code *
                </Label>
                <Input
                  id="app_code"
                  name="app_code"
                  value={formData.app_code}
                  onChange={handleChange}
                  placeholder="APPCODE"
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.app_code ? 'border-red-500' : 'border-gray-600'}`}
                  disabled={!!application}
                />
                {errors.app_code && <p className="mt-2 text-sm text-red-400">{errors.app_code}</p>}
                <p className="mt-2 text-xs text-gray-500">
                  Unique identifier for the application (uppercase letters, numbers, and underscores only)
                </p>
              </div>
              
              <div>
                <Label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter application description"
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="app_url" className="block text-sm font-medium text-gray-300 mb-2">
                    Application URL
                  </Label>
                  <Input
                    id="app_url"
                    name="app_url"
                    value={formData.app_url}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.app_url ? 'border-red-500' : 'border-gray-600'}`}
                  />
                  {errors.app_url && <p className="mt-2 text-sm text-red-400">{errors.app_url}</p>}
                </div>
                
                <div>
                  <Label htmlFor="api_url" className="block text-sm font-medium text-gray-300 mb-2">
                    API URL
                  </Label>
                  <Input
                    id="api_url"
                    name="api_url"
                    value={formData.api_url}
                    onChange={handleChange}
                    placeholder="https://api.example.com"
                    className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.api_url ? 'border-red-500' : 'border-gray-600'}`}
                  />
                  {errors.api_url && <p className="mt-2 text-sm text-red-400">{errors.api_url}</p>}
                </div>
              </div>
              
              <div>
                <Label htmlFor="logo_url" className="block text-sm font-medium text-gray-300 mb-2">
                  Logo URL
                </Label>
                <Input
                  id="logo_url"
                  name="logo_url"
                  value={formData.logo_url}
                  onChange={handleChange}
                  placeholder="/assets/logos/app.png"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <Label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="development">Development</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Footer with buttons */}
          <div className="flex justify-end gap-4 px-6 py-4 border-t border-gray-700 bg-gray-800/30">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onClose()}
              disabled={isSubmitting}
              className="px-6 py-2 border border-gray-600 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : application ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return createPortal(modalContent, document.body);
};

export default ApplicationForm; 